/**
 * The P3/P4/P5 endpoints P7's screens call, wrapped over `useSession().request()` — the one
 * authenticated door (`web/src/auth/context.ts`). This module attaches no bearer/binding
 * itself and decides no authorization; it only shapes each call's path/body and reads its
 * response, the same division `auth/transport.ts` draws for the session layer beneath it.
 *
 * Every reader is defensive rather than a schema validator: a field of the wrong shape is
 * dropped (`undefined`), never thrown, because a malformed response should degrade a screen,
 * not crash it. `field`/`stringField`/`numberField` are P6's own response-reading primitives
 * (`auth/transport.ts`), reused rather than re-implemented for a second time.
 */

import {
  asIsoInstant,
  buildPath,
  isDeliveryOutcome,
  isGatePosition,
  ROUTES,
  type DeliveryOutcome,
  type DenyReason,
  type GatePosition,
  type IsoInstant,
  type MemberRole,
  type TransportKind,
} from '@selogate/core'
import type { ApiRequest, ApiResponse } from '../../auth/transport'
import { field, numberField, stringField } from '../../auth/transport'
import { isLocale, type Locale } from '../../i18n/locales'
import { CONTENDED_OPEN_REFUSALS, type ContendedOpenRefusal } from './openFailure'

/** Same "degrade, never throw" rule as `stringField` — `asIsoInstant()` throws on a malformed
 * value, so this drops it instead, the way every other reader in this module already does. */
function isoInstantField(raw: unknown, key: string): IsoInstant | undefined {
  const value = stringField(raw, key)
  if (value === undefined) return undefined
  try {
    return asIsoInstant(value)
  } catch {
    return undefined
  }
}

export type ApiRequestFn = (request: ApiRequest) => Promise<ApiResponse>

export type ApiResult<T> =
  | { readonly ok: true; readonly status: number; readonly data: T }
  | {
      readonly ok: false
      readonly status: number
      readonly code?: string
      readonly message?: string
      readonly field?: string
    }

function ok<T>(status: number, data: T): ApiResult<T> {
  return { ok: true, status, data }
}

function fail<T>(response: ApiResponse): ApiResult<T> {
  const code = stringField(response.body, 'code')
  const message = stringField(response.body, 'message')
  const fieldName = stringField(response.body, 'field')
  return {
    ok: false,
    status: response.status,
    ...(code === undefined ? {} : { code }),
    ...(message === undefined ? {} : { message }),
    ...(fieldName === undefined ? {} : { field: fieldName }),
  }
}

function isSuccess(status: number): boolean {
  return status >= 200 && status < 300
}

const DENY_REASONS: ReadonlySet<string> = new Set([
  'subscription-expired',
  'grant-expired',
  'grant-out-of-schedule',
  'gate-latched',
  'transport-not-permitted',
])

/** `HttpError`'s wire shape is `{code, message}` (`packages/functions/auth/router.ts`). */
function denyReasonOf(result: ApiResult<unknown>): DenyReason | undefined {
  if (result.ok || result.code === undefined || !DENY_REASONS.has(result.code)) return undefined
  return result.code as DenyReason
}

/**
 * R1-7(d) — refusal shapes the open command answers with that are **not** `evaluateAccess()`
 * clauses: D129's honest `409 gate-busy` (the distributed gate-lease queue is full),
 * `gate-command`'s own `403 not-authorized` check, and D124's `409 open-in-flight`
 * (`packages/functions/gate-command/open.ts`). None belongs in core's frozen `DenyReason` — that
 * enum is `evaluateAccess()`'s clauses alone — so this widens the *local, open-command-only* type
 * rather than the shared one. Without this, `denyReasonOf()` above returns `undefined` and the
 * caller falls through to a generic "couldn't open" message inviting a retry that can only ever
 * repeat the same refusal.
 *
 * **`open-in-flight` was the missing one, and its fall-through did more than misword a line**
 * (R4). The generic branch on both screens also sets `CloudVerdict: 'ambiguous'`, which draws the
 * Bluetooth offer — so the app invited a **second dispatch against a command it had just been
 * told is still running**, which is the one thing PEC-13 forbids outright. Naming it here is what
 * lets both screens leave the verdict null.
 */
/**
 * **D294 — `gate-closing` is in BOTH unions and is the only member that is, which is the whole of
 * this decision reaching the client.**
 *
 * It names one fact — *a close holds the gate lease* — and the two routes answer it oppositely: an
 * Open's goal is not met (Failed, with its own sentence) and a Close's is (Success, C6 n=3 having
 * measured a Close onto a closing leaf as ignored-and-completing). That is not a member meaning two
 * things, which is what D274 refused to widen `reason` for; it is one fact two callers read
 * differently, which is what two unions are for.
 */
export type OpenRefusal =
  DenyReason | 'gate-busy' | 'not-authorized' | 'open-in-flight' | 'gate-closing' | ContendedOpenRefusal
const OPEN_REFUSALS: ReadonlySet<string> = new Set<string>([
  ...DENY_REASONS,
  'gate-busy',
  'not-authorized',
  'open-in-flight',
  'gate-closing',
  ...CONTENDED_OPEN_REFUSALS,
])

/**
 * **R7/D277 (f) — the close's refusals are the open's PLUS ONE, and it is a separate union
 * because widening the open's was tried and the compiler refused it.**
 *
 * The first draft added `close-not-supported` to {@link OpenRefusal}, on the reasoning that a member
 * no handler emits costs a client nothing. **That was false and `tsc` said so**: both open screens
 * end their refusal chain with an `else` that means *what remains is a `DenyReason`*, which is a
 * true invariant **of the open path** and stops being one the moment a member the open route
 * cannot return joins the union. The cost of widening was a branch on two screens for a value
 * neither can ever receive — the shape D274 refuses one layer down, where a widened `reason` would
 * make every client switch on members meaning two different things.
 *
 * So the close carries its own union. A resident should never see this refusal: the control is
 * drawn only where the gate reads `command`. It exists because **the mode can change under an open
 * Gate Detail and a client is not the safety layer**, which is exactly why a client has to be able
 * to name it.
 */
/**
 * **D297 makes the two unions SIBLINGS rather than a subset chain, and it is D277 (f)'s own
 * argument arriving from the other side.**
 *
 * This read `OpenRefusal | 'close-not-supported'` while the open's set was the smaller of the two.
 * D297 gives the open route three refusal codes a close can never return — they are the *contended
 * open's* verdicts, and `close.ts` answers `gate-busy` for every **open** holder whatever that open
 * did (its own note says why) — so the derivation would put three unreachable members into the
 * close's chain and break the `else` that means *what remains is a `DenyReason`*. **D294 then made
 * the two sets overlap rather than nest**, `gate-closing` being the one code both routes can
 * return, which a subset chain could not have expressed either. That is exactly the cost D277 (f)
 * refused to pay in the other direction, and `tsc` refuses it here too.
 *
 * So each route names its own set. The duplication is four members; what it buys is that each
 * screen's refusal chain ends in an `else` that is true of the route it is reading.
 */
export type CloseRefusal =
  DenyReason | 'gate-busy' | 'not-authorized' | 'open-in-flight' | 'close-not-supported' | 'gate-closing'
const CLOSE_REFUSALS: ReadonlySet<string> = new Set<string>([
  ...DENY_REASONS,
  'gate-busy',
  'not-authorized',
  'open-in-flight',
  'close-not-supported',
  'gate-closing',
])

function openRefusalOf(result: ApiResult<unknown>): OpenRefusal | undefined {
  if (result.ok || result.code === undefined || !OPEN_REFUSALS.has(result.code)) return undefined
  return result.code as OpenRefusal
}

function closeRefusalOf(result: ApiResult<unknown>): CloseRefusal | undefined {
  if (result.ok || result.code === undefined || !CLOSE_REFUSALS.has(result.code)) return undefined
  return result.code as CloseRefusal
}

/*
 * ---------------------------------------------------------------------------
 * P3 — gate reads/writes.
 * ---------------------------------------------------------------------------
 */

export interface LocationInput {
  readonly formatted: string
  readonly source: 'device' | 'places' | 'manual'
  readonly placeId?: string
  readonly lat?: number
  readonly lng?: number
}

export interface LocationView {
  readonly formatted: string
  readonly source: string
  readonly placeId?: string
  readonly lat?: number
  readonly lng?: number
}

export interface GateDetail {
  readonly gateId: string
  readonly defaultName: string
  readonly type: string
  readonly status: string
  readonly operatingState: string
  readonly location: LocationView
  readonly viewer: {
    readonly role: 'manager' | 'resident'
    readonly status: 'pending' | 'active' | 'expired'
    readonly installer: boolean
  }
  readonly createdAt: string
  /**
   * **`plan` and `status` are optional since R5 step 1, and `renewsAt` is not.** The wire always
   * carries all three (`readSubscription` below still refuses a partial block), but the *cache*
   * carries `renewsAt` alone — C2's deliberate narrow amendment to the snapshot allow-list — and
   * `renewsAt` is the only member this client ever reads (`expired`, `subscriptionDays`, and
   * `accessForDetail`'s `subscription-expired`). Widening the type is what lets `detailFromCache`
   * reconstruct the block from the one field it has, rather than dropping a renewal countdown a
   * manager's own device is already holding.
   *
   * Do **not** widen `readSubscription` to match: a wire block missing `plan` or `status` is a
   * malformed response, and the reader's job is to refuse one.
   */
  readonly subscription?: { readonly plan?: string; readonly status?: string; readonly renewsAt: string }
  readonly bleConfigured?: boolean
  /** The gate's last reported position, from P4's `lastState` snapshot (D134). */
  readonly lastPosition?: GatePosition
  readonly lastSeenAt?: string
  readonly unseenExpired?: number
  readonly alias?: string
  readonly icon?: string
  readonly color?: string
  readonly sortOrder?: number
  /**
   * D153 — manager-only (absent for a resident, absent from any bundle deployed before this
   * shipped). Treat absence as *unknown*, never as `1`: `GateDetailScreen.tsx`'s Remove-me row
   * stays present until a *confirmed* `1` says otherwise, the same fail-open rule D143 already
   * applies to the roster read this seeds.
   */
  readonly managerCount?: number
  /**
   * P8's `shabbatAutomation` (§9), passed through P3's one gate projection with **no role
   * check** — every viewer, manager or resident, sees whether the automation is configured
   * (`packages/functions/provision/projection.ts:76,119`). D46 means the attribute is present
   * only when configured, so its presence *is* "on"; this reader collapses that to a boolean
   * because Gate Detail's read-only row (D112-D115/D120) needs nothing else — the panel that
   * needs the rest (`mode`/`schedule`/last-run) reads P8's own manager-only `GET
   * /gates/{id}/shabbat` instead (see `getShabbatConfig` below).
   *
   * **D174 broke "presence is on" and the reader below is where that is repaired.** Turning
   * שעון שבת off now *suspends* the record rather than deleting it, so the configuration a
   * manager set survives to seed the form next time — which means a present `shabbatAutomation`
   * with `suspended: true` is **off**. Reading presence alone would light Gate Detail's
   * "מתוזמן" segment on a gate whose automation is not running, and a segmented control cannot
   * show two selections.
   *
   * **The cached path carries it since R5 step 2** — `CachedGate.shabbatEnabled` is the same
   * derivation applied to the same field off `GET /gates`, and `detailFromCache` maps it back.
   * It is still **absent, never `false`**, for a gate whose snapshot predates that change, and
   * D135's rule for an unknown value is to render nothing rather than to guess "off".
   */
  readonly shabbatEnabled?: boolean
  /**
   * **R7/D276 — how the gate closes (renamed by D279), and absence is a real state.**
   *
   * `'command'` means the technician configured the controller to close on command,
   * so the app may offer a Close; `'auto'` means it closes by itself after an open and the
   * open-only surface is correct. **Absent reads as `auto`** — every gate provisioned before
   * D276, and every response from a bundle deployed before it — and that default is resolved in
   * exactly one place, {@link closeModeOf}, never at a call site.
   *
   * It reaches every role from P3's one gate projection, deliberately: the client that has to
   * decide whether to draw the control is a resident's, and Home issues no gate read of its own
   * (D141), so a manager-only field could not be rendered where it is needed.
   */
  readonly closeMode?: CloseMode
}

/** R7/D276 — the two modes, as the wire spells them. */
export type CloseMode = 'auto' | 'command'

/**
 * **The `auto` default, resolved in ONE place.**
 *
 * Both `GateDetail` and `CachedGate` carry `closeMode` optionally and both mean the same thing by
 * its absence, so a `?? 'auto'` at each reader is a default that eventually disagrees with
 * itself — which is what D200's `isLocale()` sweep found five copies of, and what
 * `directionOfRecord` avoids on the server. Every consumer asks this.
 *
 * **An unrecognised value reads `auto`, not `command`.** A newer server naming a third mode
 * must not switch on a capability this client cannot render, and `auto` is the behaviour every
 * gate already has.
 */
export function closeModeOf(gate: { readonly closeMode?: string } | undefined): CloseMode {
  return gate?.closeMode === 'command' ? 'command' : 'auto'
}

function readLocation(raw: unknown): LocationView {
  const formatted = stringField(raw, 'formatted') ?? ''
  const source = stringField(raw, 'source') ?? 'manual'
  const placeId = stringField(raw, 'placeId')
  const lat = numberField(raw, 'lat')
  const lng = numberField(raw, 'lng')
  return {
    formatted,
    source,
    ...(placeId === undefined ? {} : { placeId }),
    ...(lat === undefined ? {} : { lat }),
    ...(lng === undefined ? {} : { lng }),
  }
}

/** `position` off a `GateStateSnapshot`-shaped value, validated by core's one guard. */
function readPosition(raw: unknown): GatePosition | undefined {
  const position = field(raw, 'position')
  return isGatePosition(position) ? position : undefined
}

function readSubscription(raw: unknown): GateDetail['subscription'] {
  if (raw === undefined) return undefined
  const plan = stringField(raw, 'plan')
  const status = stringField(raw, 'status')
  const renewsAt = stringField(raw, 'renewsAt')
  if (plan === undefined || status === undefined || renewsAt === undefined) return undefined
  return { plan, status, renewsAt }
}

function readGateDetail(body: unknown): GateDetail | undefined {
  const gateId = stringField(body, 'gateId')
  const defaultName = stringField(body, 'defaultName')
  const viewer = field(body, 'viewer')
  const role = field(viewer, 'role')
  const status = field(viewer, 'status')
  if (
    gateId === undefined ||
    defaultName === undefined ||
    (role !== 'manager' && role !== 'resident') ||
    (status !== 'pending' && status !== 'active' && status !== 'expired')
  ) {
    return undefined
  }
  const unseenExpired = numberField(body, 'unseenExpired')
  const subscription = readSubscription(field(body, 'subscription'))
  const bleConfiguredRaw = field(body, 'bleConfigured')
  // `lastState` is P4's `GateStateSnapshot` object, not a string — `stringField` dropped it every
  // time. Only `position` is read; `firmware`/`alarm` are not this screen's business (Q28).
  const lastPosition = readPosition(field(body, 'lastState'))
  const lastSeenAt = stringField(body, 'lastSeenAt')
  const alias = stringField(body, 'alias')
  const icon = stringField(body, 'icon')
  const color = stringField(body, 'color')
  const sortOrder = numberField(body, 'sortOrder')
  const managerCount = numberField(body, 'managerCount')
  // R7/D276 — narrowed against the union rather than passed through, `deliveryOutcome`'s own
  // rule: an unrecognised value from a newer server must read as *did not say*, which here is
  // absent, which `closeModeOf` resolves to `auto`.
  const closeModeRaw = stringField(body, 'closeMode')
  const closeMode = closeModeRaw === 'command' || closeModeRaw === 'auto' ? closeModeRaw : undefined
  return {
    gateId,
    defaultName,
    type: stringField(body, 'type') ?? 'barrier',
    status: stringField(body, 'status') ?? 'unknown',
    operatingState: stringField(body, 'operatingState') ?? 'normal',
    location: readLocation(field(body, 'location')),
    viewer: { role, status, installer: field(viewer, 'installer') === true },
    createdAt: stringField(body, 'createdAt') ?? '',
    // D46 + D174: present only when configured, **and not suspended** — see the field's own doc
    // comment on `GateDetail` for why presence alone stopped being the signal.
    shabbatEnabled:
      field(body, 'shabbatAutomation') !== undefined &&
      field(field(body, 'shabbatAutomation'), 'suspended') !== true,
    ...(subscription === undefined ? {} : { subscription }),
    ...(bleConfiguredRaw === undefined ? {} : { bleConfigured: bleConfiguredRaw === true }),
    ...(lastPosition === undefined ? {} : { lastPosition }),
    ...(lastSeenAt === undefined ? {} : { lastSeenAt }),
    ...(unseenExpired === undefined ? {} : { unseenExpired }),
    ...(alias === undefined ? {} : { alias }),
    ...(icon === undefined ? {} : { icon }),
    ...(color === undefined ? {} : { color }),
    ...(sortOrder === undefined ? {} : { sortOrder }),
    ...(managerCount === undefined ? {} : { managerCount }),
    ...(closeMode === undefined ? {} : { closeMode }),
  }
}

export async function getGate(request: ApiRequestFn, gateId: string): Promise<ApiResult<GateDetail>> {
  const response = await request({
    method: ROUTES.gateRead.method,
    path: buildPath(ROUTES.gateRead, { gateId }),
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readGateDetail(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

export async function patchGateName(
  request: ApiRequestFn,
  gateId: string,
  defaultName: string,
): Promise<ApiResult<GateDetail>> {
  const response = await request({
    method: ROUTES.gateUpdate.method,
    path: buildPath(ROUTES.gateUpdate, { gateId }),
    body: { defaultName },
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readGateDetail(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

/**
 * **R7/D279 — the two shapes `PATCH /gates/{gateId}` is called with, as a union rather than as
 * three optional fields.**
 *
 * D176's naming editor sends the name and the address **together** — one audit row and one run of
 * P8's rebuild hook — and that pairing is a rule rather than a convenience, so the first arm keeps
 * both required. The update menu's close-mode pill sends `closeMode` alone. Three optional fields
 * would express both *and* the empty body the route refuses with a `422`, which is a refusal this
 * client should not be able to reach.
 */
export type GateDetailsPatch =
  | {
      readonly defaultName: string
      readonly location: LocationInput
      readonly closeMode?: CloseMode
    }
  | { readonly closeMode: CloseMode }

/**
 * D176 — `PATCH /gates/{gateId}` with **both** editable fields in one call, for the install
 * flow's manager update branch.
 *
 * Separate from `patchGateName` above rather than an optional second argument to it, because the
 * two are different edits: Device settings renames a gate, and this one re-states the whole of
 * what §7.8's naming step collects. Sending them together is also the honest shape — one audit
 * row, and **one** run of P8's D66 rebuild-on-location-edit hook, where two calls would write two
 * of each for a single user action.
 *
 * Re-saving an unchanged address is not a no-op server-side: the route stamps `enteredBy`/
 * `enteredAt` from the caller, so whoever last opened this screen owns the row. That is the
 * route's own stated rule (`provision/routes.ts`), not a side effect of this helper.
 */
export async function patchGateDetails(
  request: ApiRequestFn,
  gateId: string,
  body: GateDetailsPatch,
): Promise<ApiResult<GateDetail>> {
  const response = await request({
    method: ROUTES.gateUpdate.method,
    path: buildPath(ROUTES.gateUpdate, { gateId }),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readGateDetail(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

/**
 * A stored {@link LocationView} narrowed back into the {@link LocationInput} the picker edits.
 *
 * The one lossy step is `source`: the view widens it to `string` because the server owns the
 * vocabulary, and an unrecognized provenance becomes `manual` — which is what a value nobody can
 * attribute honestly is. `placeId`/`lat`/`lng` are carried through untouched, so a manager who
 * opens the screen and saves without editing does not silently drop the gate's coordinates.
 */
export function locationInputFromView(view: LocationView): LocationInput {
  const source: LocationInput['source'] =
    view.source === 'device' || view.source === 'places' ? view.source : 'manual'
  return {
    formatted: view.formatted,
    source,
    ...(view.placeId === undefined ? {} : { placeId: view.placeId }),
    ...(view.lat === undefined ? {} : { lat: view.lat }),
    ...(view.lng === undefined ? {} : { lng: view.lng }),
  }
}

export interface ProvisionBody {
  readonly code: string
  readonly mode: 'independent'
  readonly defaultName: string
  readonly type: 'barrier' | 'door' | 'parking' | 'garage'
  readonly location: LocationInput
  readonly connectivity: { readonly kind: 'wifi'; readonly ssid: string } | { readonly kind: 'sim' }
  /**
   * **R7/D279 — named here, and required, because a spread cannot be trusted to carry it.**
   *
   * A spread into an object literal bypasses TypeScript's excess-property check (D232's build
   * finding), so a `closeMode` handed to `provisionGate` through a body shape that does not name
   * the field compiles clean and is dropped on the floor — the failure `provisioning.ts`'s own
   * `StoredGate` names it against, one layer up. It is **required** rather than optional for the
   * same reason: the install flow now has a step that collects it, and an omission is a screen
   * that stopped asking rather than a gate that has no answer.
   */
  readonly closeMode: CloseMode
}

export interface ProvisionConflict {
  readonly code: 'gate-already-provisioned'
  readonly gateId: string
  readonly defaultName: string
  readonly installedByUserId: string
  readonly installedAt: string
  readonly viewer?: GateDetail['viewer']
}

function readProvisionConflict(body: unknown): ProvisionConflict | undefined {
  const code = stringField(body, 'code')
  const gateId = stringField(body, 'gateId')
  const defaultName = stringField(body, 'defaultName')
  const installedByUserId = stringField(body, 'installedByUserId')
  const installedAt = stringField(body, 'installedAt')
  if (
    code !== 'gate-already-provisioned' ||
    gateId === undefined ||
    defaultName === undefined ||
    installedByUserId === undefined ||
    installedAt === undefined
  )
    return undefined
  const viewer = field(body, 'viewer')
  const role = field(viewer, 'role')
  const status = field(viewer, 'status')
  const parsedViewer: GateDetail['viewer'] | undefined =
    (role === 'manager' || role === 'resident') &&
    (status === 'pending' || status === 'active' || status === 'expired')
      ? { role, status, installer: field(viewer, 'installer') === true }
      : undefined
  return {
    code,
    gateId,
    defaultName,
    installedByUserId,
    installedAt,
    ...(parsedViewer === undefined ? {} : { viewer: parsedViewer }),
  }
}

export async function provisionGate(
  request: ApiRequestFn,
  body: ProvisionBody,
): Promise<ApiResult<GateDetail | ProvisionConflict>> {
  const response = await request({
    method: ROUTES.gateProvision.method,
    path: buildPath(ROUTES.gateProvision),
    body,
  })
  if (response.status === 409) {
    const conflict = readProvisionConflict(response.body)
    return conflict === undefined ? fail(response) : ok(response.status, conflict)
  }
  if (!isSuccess(response.status)) return fail(response)
  const data = readGateDetail(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

/**
 * D151 — `GET /gates/claim/{brokerCode}`, a read-only courtesy in front of `provisionGate`'s
 * `409`. It lets the install flow show "already installed" right after the scan, before a name
 * or address is typed — **never a replacement for the `409`**, which stays the authoritative
 * answer at submit time.
 *
 * `claimed: true`'s body is exactly `provisionGate`'s `409` body plus the discriminator, so this
 * reuses `readProvisionConflict()` rather than a second parser.
 */
export async function lookupBrokerClaim(
  request: ApiRequestFn,
  brokerCode: string,
): Promise<ApiResult<{ readonly claimed: false } | ({ readonly claimed: true } & ProvisionConflict)>> {
  const response = await request({
    method: ROUTES.gateClaimLookup.method,
    path: buildPath(ROUTES.gateClaimLookup, { brokerCode }),
  })
  if (!isSuccess(response.status)) return fail(response)
  if (field(response.body, 'claimed') === false) return ok(response.status, { claimed: false })
  const conflict = readProvisionConflict(response.body)
  return conflict === undefined ? fail(response) : ok(response.status, { claimed: true, ...conflict })
}

export async function putBleCredentials(
  request: ApiRequestFn,
  gateId: string,
  body: { readonly bleName: string; readonly blePassword: string; readonly bleTestPassed?: boolean },
): Promise<ApiResult<{ readonly bleConfigured: boolean }>> {
  const response = await request({
    method: ROUTES.gateBleCredentials.method,
    path: buildPath(ROUTES.gateBleCredentials, { gateId }),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)
  return ok(response.status, { bleConfigured: field(response.body, 'bleConfigured') === true })
}

/**
 * **`GET /gates/{id}/subscription` has no client caller (D246).** The Billing screen was the only
 * one, and D246 deleted it: the renewal countdown a manager needs is the days-remaining badge on
 * Gate Detail's Subscription row, computed from `gate.subscription.renewsAt` in the gate projection
 * P3 already returns. The route stays deployed and `devTransport` keeps mocking it — that module
 * fakes the *server*, and the server still serves this — but nothing in `web/` reads it, so the
 * reader and its type are gone rather than left as an export with no consumer.
 * `infra/tests/route-coverage.test.ts` is unaffected: it asserts client -> registry and
 * registry -> edge, and dropping a client caller breaks neither direction.
 */

/**
 * `POST /gates/{id}/state` — D40's latch and release, shipped by P8
 * (`packages/functions/state/routes.ts:135-178`). Stage 1 accepts only `normal` and
 * `perm-closed`, the two values this signature carries; `perm-open` and `pedestrian` are refused
 * `501 state-reserved` and never persisted (D87), and this client sends neither today (Q23 is
 * open and may not be guessed). A `501` still needs its own branch at the call site — the
 * refusal is audit-logged like any other rejected command, not folded into the generic failure
 * (R3-3/D-A, superseding the guessed `PATCH .../latch` seam recorded in AGENTS.md and
 * implementation-notes.md → P7, Deviation 4).
 */
export async function latchGate(
  request: ApiRequestFn,
  gateId: string,
  state: 'normal' | 'perm-closed',
): Promise<ApiResult<{ readonly operatingState: string }>> {
  const response = await request({
    method: ROUTES.gateState.method,
    path: buildPath(ROUTES.gateState, { gateId }),
    body: { state },
  })
  if (!isSuccess(response.status)) return fail(response)
  return ok(response.status, { operatingState: stringField(response.body, 'operatingState') ?? state })
}

/*
 * ---------------------------------------------------------------------------
 * P8 — the שעון שבת panel's own reads/writes (D41/D56/D100), manager-only.
 *
 * `GET /gates/{id}/shabbat` is the one P8 route with no `flows.md`/§9 entry — it exists because
 * P3's gate projection (what `GateDetail.shabbatEnabled` above reads) carries no last-run outcome
 * or seen-flag, and those are manager-only (`packages/functions/state/routes.ts:181-186`).
 * ---------------------------------------------------------------------------
 */

/** D100's last-run record, mirroring `packages/functions/state/records.ts`'s `ShabbatLastRun`. */
export interface ShabbatLastRun {
  readonly at: IsoInstant
  readonly edge: 'entry' | 'exit'
  readonly outcome: 'succeeded' | 'failed'
  /** Present on a failure only — a short machine reason, never echoed verbatim as a stack. */
  readonly reason?: string
  readonly seenAt?: IsoInstant
}

/** Mirrors `packages/functions/state/routes.ts`'s `ShabbatView` — what the manager-only read returns. */
export interface ShabbatConfig {
  readonly gateId: string
  /** **The automation is running.** D174: false on a suspended record, which is still stored. */
  readonly configured: boolean
  /**
   * D174 — a stored configuration exists, running or suspended. The panel seeds its form from a
   * retained record, which is what makes "turn it off, turn it back on" keep a manager's days,
   * times, offsets and city instead of returning them to the defaults.
   */
  readonly retained: boolean
  readonly schedule?: 'hebcal' | 'manual'
  readonly mode?: string
  readonly entryOffset?: number
  readonly exitOffset?: number
  readonly days?: readonly string[]
  readonly entryTime?: string
  readonly exitTime?: string
  /** A Hebcal locality geonameid — never a coordinate (D108). Shown, not geocoded, by this client. */
  readonly geo?: number
  readonly geoFallback?: boolean
  /** D174 — `'manual'` when a manager named the city; absent/`'auto'` when the address matched. */
  readonly geoSource?: 'auto' | 'manual'
  readonly timeZone?: string
  readonly nextEntryAt?: IsoInstant
  readonly nextExitAt?: IsoInstant
  readonly lastRun?: ShabbatLastRun
  readonly unseenFailure?: boolean
  /**
   * G-P8-a — the gate is latched and the latch is the automation's (`operatingState !== 'normal'`
   * and `operatingStateVia === 'shabbat-automation'`, projected server-side). A save whose window
   * is not in progress releases such a gate at once, which is what the save confirmation says.
   * Both `GET` and `PUT` carry it, and they answer different questions: `GET`'s is the gate before
   * a save, `PUT`'s the gate after it — so a warning about what a save will do reads `GET`'s.
   *
   * **Absent reads as `false`, and that direction is the rule**: a server that predates the field
   * cannot tell this client the latch is the automation's, and "releases now" over a gate a manager
   * closed by hand is a promise the server will not keep.
   */
  readonly latchedByAutomation: boolean
}

function readShabbatLastRun(raw: unknown): ShabbatLastRun | undefined {
  const at = isoInstantField(raw, 'at')
  const edge = field(raw, 'edge')
  const outcome = field(raw, 'outcome')
  if (
    at === undefined ||
    (edge !== 'entry' && edge !== 'exit') ||
    (outcome !== 'succeeded' && outcome !== 'failed')
  ) {
    return undefined
  }
  const reason = stringField(raw, 'reason')
  const seenAt = isoInstantField(raw, 'seenAt')
  return {
    at,
    edge,
    outcome,
    ...(reason === undefined ? {} : { reason }),
    ...(seenAt === undefined ? {} : { seenAt }),
  }
}

function readShabbatConfig(body: unknown): ShabbatConfig | undefined {
  const gateId = stringField(body, 'gateId')
  if (gateId === undefined) return undefined
  const scheduleRaw = field(body, 'schedule')
  const schedule = scheduleRaw === 'hebcal' || scheduleRaw === 'manual' ? scheduleRaw : undefined
  const mode = stringField(body, 'mode')
  const entryOffset = numberField(body, 'entryOffset')
  const exitOffset = numberField(body, 'exitOffset')
  const daysRaw = field(body, 'days')
  const days = Array.isArray(daysRaw)
    ? daysRaw.filter((day): day is string => typeof day === 'string')
    : undefined
  const entryTime = stringField(body, 'entryTime')
  const exitTime = stringField(body, 'exitTime')
  const geo = numberField(body, 'geo')
  const geoFallback = field(body, 'geoFallback')
  const geoSourceRaw = field(body, 'geoSource')
  const geoSource = geoSourceRaw === 'auto' || geoSourceRaw === 'manual' ? geoSourceRaw : undefined
  const timeZone = stringField(body, 'timeZone')
  const nextEntryAt = isoInstantField(body, 'nextEntryAt')
  const nextExitAt = isoInstantField(body, 'nextExitAt')
  const lastRun = readShabbatLastRun(field(body, 'lastRun'))
  const unseenFailure = field(body, 'unseenFailure')
  return {
    gateId,
    configured: field(body, 'configured') === true,
    // Absent on a server that predates D174, where a projected record was always a running one.
    retained: field(body, 'retained') === true || field(body, 'configured') === true,
    ...(schedule === undefined ? {} : { schedule }),
    ...(mode === undefined ? {} : { mode }),
    ...(entryOffset === undefined ? {} : { entryOffset }),
    ...(exitOffset === undefined ? {} : { exitOffset }),
    ...(days === undefined ? {} : { days }),
    ...(entryTime === undefined ? {} : { entryTime }),
    ...(exitTime === undefined ? {} : { exitTime }),
    ...(geo === undefined ? {} : { geo }),
    ...(typeof geoFallback === 'boolean' ? { geoFallback } : {}),
    ...(geoSource === undefined ? {} : { geoSource }),
    ...(timeZone === undefined ? {} : { timeZone }),
    ...(nextEntryAt === undefined ? {} : { nextEntryAt }),
    ...(nextExitAt === undefined ? {} : { nextExitAt }),
    ...(lastRun === undefined ? {} : { lastRun }),
    ...(typeof unseenFailure === 'boolean' ? { unseenFailure } : {}),
    // `=== true`, never `!== false`: absent, `null` and a malformed value all mean "not known to be
    // the automation's", which is the only answer that cannot promise a release (see the type).
    latchedByAutomation: field(body, 'latchedByAutomation') === true,
  }
}

/**
 * **D175's preview** — what `GET /gates/{id}/shabbat?preview=hebcal…` answers with, mirroring
 * `packages/functions/state/routes.ts`'s `ShabbatPreview`.
 *
 * It is the `hebcal` fork's answer to "what would this configuration actually do", computed
 * before anything is saved. `manual` has no preview and needs none: its days and clock times are
 * the manager's own, and the panel can state them exactly without asking the server.
 *
 * **`entryAt`/`exitAt` are absent together** when the feed returned nothing usable or could not
 * be reached. The locality is still answered in that case — it resolves locally (D108) — so the
 * panel can name the city it would ask about even when it cannot say when.
 */
export interface ShabbatPreview {
  /** The locality that would be asked — a geoname, never a coordinate (D108). */
  readonly geo?: number
  readonly geoFallback?: boolean
  /** `'manual'` when the request named a city; absent when the gate's address resolved one. */
  readonly geoSource?: 'auto' | 'manual'
  readonly timeZone?: string
  readonly entryAt?: IsoInstant
  readonly exitAt?: IsoInstant
}

/**
 * The one parameter the preview varies on.
 *
 * **The offsets are deliberately not here** (D175): the preview answers *the calendar's*
 * candle-lighting and havdalah, which is what the panel's two rows show and what a nudge is
 * measured against. The nudged pair is those instants plus a whole number of minutes, applied
 * where it is read — the save confirmation.
 */
export interface ShabbatPreviewInput {
  /** Omitted to preview the **automatic** path, exactly as the write omits it (D46/D174). */
  readonly geonameid?: number
}

/** `ApiRequest.path` carries no separate query field — built here, like `activityQueryString`. */
function shabbatPreviewQueryString(input: ShabbatPreviewInput): string {
  const query = new URLSearchParams()
  query.set('preview', 'hebcal')
  if (input.geonameid !== undefined) query.set('geonameid', String(input.geonameid))
  return `?${query.toString()}`
}

function readShabbatPreview(body: unknown): ShabbatPreview | undefined {
  const raw = field(body, 'preview')
  if (raw === undefined || raw === null) return undefined
  const geo = numberField(raw, 'geo')
  const geoFallback = field(raw, 'geoFallback')
  const geoSourceRaw = field(raw, 'geoSource')
  const geoSource = geoSourceRaw === 'auto' || geoSourceRaw === 'manual' ? geoSourceRaw : undefined
  const timeZone = stringField(raw, 'timeZone')
  const entryAt = isoInstantField(raw, 'entryAt')
  const exitAt = isoInstantField(raw, 'exitAt')
  return {
    ...(geo === undefined ? {} : { geo }),
    ...(typeof geoFallback === 'boolean' ? { geoFallback } : {}),
    ...(geoSource === undefined ? {} : { geoSource }),
    ...(timeZone === undefined ? {} : { timeZone }),
    ...(entryAt === undefined ? {} : { entryAt }),
    ...(exitAt === undefined ? {} : { exitAt }),
  }
}

/**
 * D175 — the `hebcal` preview.
 *
 * **The same route as the configuration read**, with a query. It is a read in every sense: it
 * stores nothing, arms nothing, and leaves a gate with no configuration still having none. What
 * it answers with is the **unnudged** pair — see `ShabbatPreviewInput`. A
 * server that predates D175 answers the read without a `preview` object, which reads here as
 * `undefined` — the panel then simply has no times to show, exactly as it had before.
 */
export async function getShabbatPreview(
  request: ApiRequestFn,
  gateId: string,
  input: ShabbatPreviewInput,
): Promise<ApiResult<ShabbatPreview | undefined>> {
  const response = await request({
    method: ROUTES.shabbatRead.method,
    path: buildPath(ROUTES.shabbatRead, { gateId }) + shabbatPreviewQueryString(input),
  })
  if (!isSuccess(response.status)) return fail(response)
  return ok(response.status, readShabbatPreview(response.body))
}

export async function getShabbatConfig(
  request: ApiRequestFn,
  gateId: string,
): Promise<ApiResult<ShabbatConfig>> {
  const response = await request({
    method: ROUTES.shabbatRead.method,
    path: buildPath(ROUTES.shabbatRead, { gateId }),
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readShabbatConfig(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

/** `PUT /gates/{id}/shabbat`'s body — the fork validated as a fork, same shape as the server's `ShabbatBody`. */
export type ShabbatConfigInput =
  | {
      readonly schedule: 'hebcal'
      readonly mode: 'perm-closed'
      readonly entryOffset?: number
      readonly exitOffset?: number
      /**
       * D174 — the manager's own city. **Omitted, never `null`, to mean "keep resolving from the
       * address"** (D46): the server treats absence as the automatic path, so sending a value the
       * manager did not choose would freeze a locality they never picked.
       */
      readonly geonameid?: number
    }
  | {
      readonly schedule: 'manual'
      readonly mode: 'perm-closed'
      readonly days: readonly string[]
      readonly entryTime: string
      readonly exitTime: string
    }

export async function putShabbatConfig(
  request: ApiRequestFn,
  gateId: string,
  body: ShabbatConfigInput,
): Promise<ApiResult<ShabbatConfig>> {
  const response = await request({
    method: ROUTES.shabbatSet.method,
    path: buildPath(ROUTES.shabbatSet, { gateId }),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)
  const data = readShabbatConfig(response.body)
  return data === undefined ? fail(response) : ok(response.status, data)
}

/**
 * `DELETE /gates/{id}/shabbat` — **turns the automation off and keeps its configuration** (D174).
 *
 * The verb still deletes something: the schedules and the armed window. What it no longer deletes
 * is the manager's days, times, offsets and city, which the server retains so selecting "מתוזמן"
 * again offers them back.
 *
 * **Does not release the gate** (P8's own doc comment): a manager who latched the gate on purpose
 * keeps that latch, unaffected. Since D174 the caller is Gate Detail's four-pill control, which
 * writes the operating state in the same gesture.
 */
export async function suspendShabbatConfig(request: ApiRequestFn, gateId: string): Promise<ApiResult<void>> {
  const response = await request({
    method: ROUTES.shabbatClear.method,
    path: buildPath(ROUTES.shabbatClear, { gateId }),
  })
  return isSuccess(response.status) ? ok(response.status, undefined) : fail(response)
}

/**
 * `POST /gates/{id}/shabbat/seen` — D100's seen-flag. **Only this panel calls it.** Gate Detail's
 * read-only row must never call this: viewing the On/Off line is not seeing the failure
 * (`packages/functions/state/routes.ts:277-280`).
 */
export async function markShabbatSeen(request: ApiRequestFn, gateId: string): Promise<void> {
  await request({ method: ROUTES.shabbatSeen.method, path: buildPath(ROUTES.shabbatSeen, { gateId }) })
}

/*
 * ---------------------------------------------------------------------------
 * P4 — the open command.
 * ---------------------------------------------------------------------------
 */

export interface OpenOutcome {
  readonly status: 'completed' | 'pending'
  readonly outcome?: 'opened' | 'refused' | 'failed'
  readonly transport?: string
  readonly reason?: DenyReason
  /** What the gate said its leaf was doing when it acknowledged (D134). Often `closed`. */
  readonly position?: GatePosition
  /**
   * **D274 — what the transport observed.** Absent means *this server did not say*, which is a
   * replay off a pre-D274 record or a bundle deployed before it — never `'timeout'`, and never a
   * licence to draw a sentence. `renderedDeliveryFailure()` is the only thing that reads it.
   */
  readonly deliveryOutcome?: DeliveryOutcome
  /**
   * Echoed back by the server, and the token the caller presents as `confirmFor` on the follow-up
   * status refreshes (D134). Read from the response rather than remembered from the request, so
   * a replay confirms the open that actually happened.
   */
  readonly idempotencyKey?: string
}

function readOpenOutcome(body: unknown): OpenOutcome | undefined {
  const status = field(body, 'status')
  if (status !== 'completed' && status !== 'pending') return undefined
  const result = field(body, 'result')
  const outcome = field(result, 'outcome')
  const reason = stringField(result, 'reason')
  const transport = stringField(result, 'transport')
  const position = field(result, 'position')
  const deliveryOutcome = stringField(result, 'deliveryOutcome')
  const idempotencyKey = stringField(body, 'idempotencyKey')
  return {
    status,
    ...(idempotencyKey === undefined ? {} : { idempotencyKey }),
    ...(outcome === 'opened' || outcome === 'refused' || outcome === 'failed' ? { outcome } : {}),
    ...(transport === undefined ? {} : { transport }),
    ...(reason !== undefined && DENY_REASONS.has(reason) ? { reason: reason as DenyReason } : {}),
    ...(isGatePosition(position) ? { position } : {}),
    // Narrowed against the union rather than passed through: an unrecognised value from a newer
    // server must read as *did not say* rather than reach a lookup that has no key for it.
    ...(isDeliveryOutcome(deliveryOutcome) ? { deliveryOutcome } : {}),
  }
}

function newIdempotencyKey(): string {
  return `p7-${globalThis.crypto.randomUUID()}`
}

export async function openGate(
  request: ApiRequestFn,
  gateId: string,
  pedestrian: boolean,
): Promise<ApiResult<OpenOutcome> & { readonly reason?: OpenRefusal }> {
  const response = await request({
    method: ROUTES.gateOpen.method,
    path: buildPath(ROUTES.gateOpen, { gateId }),
    body: { idempotencyKey: newIdempotencyKey(), ...(pedestrian ? { pedestrian: true } : {}) },
  })
  if (!isSuccess(response.status)) {
    const failure = fail<OpenOutcome>(response)
    const reason = openRefusalOf(failure)
    return reason === undefined ? failure : { ...failure, reason }
  }
  const data = readOpenOutcome(response.body)
  if (data === undefined) return fail(response)
  return data.reason === undefined
    ? ok(response.status, data)
    : { ...ok(response.status, data), reason: data.reason }
}

/**
 * **R7/D277 — `POST /gates/{gateId}/close`, beside {@link openGate} and never a flag on it.**
 *
 * Same envelope, same reader, same refusal narrowing — the *response* shapes are identical
 * because the server returns one `OpenResult` for both directions (`contracts.md` §2), and
 * `'opened'` has meant *the controller received and answered* since D134 rather than naming a
 * direction. What the two do not share is which outcomes are Success, which is `openFailure.ts`'s
 * business and not this module's.
 *
 * **A separate function rather than `openGate(request, gateId, pedestrian, direction)`**, on
 * D277 (a)'s own argument one layer up: the two routes invert two of D223's four rows, and a
 * direction parameter is what lets a call site pass the wrong one.
 *
 * 🔸 **`pedestrian: true` sends `CMD:CloseP`, which has never been sent to a controller** — R7
 * S1's C9/C9c were not run (`measurements.md` §3.1 W12).
 */
export async function closeGate(
  request: ApiRequestFn,
  gateId: string,
  pedestrian: boolean,
): Promise<ApiResult<OpenOutcome> & { readonly reason?: CloseRefusal }> {
  const response = await request({
    method: ROUTES.gateClose.method,
    path: buildPath(ROUTES.gateClose, { gateId }),
    body: { idempotencyKey: newIdempotencyKey(), ...(pedestrian ? { pedestrian: true } : {}) },
  })
  if (!isSuccess(response.status)) {
    const failure = fail<OpenOutcome>(response)
    const reason = closeRefusalOf(failure)
    return reason === undefined ? failure : { ...failure, reason }
  }
  const data = readOpenOutcome(response.body)
  if (data === undefined) return fail(response)
  return data.reason === undefined
    ? ok(response.status, data)
    : { ...ok(response.status, data), reason: data.reason }
}

/*
 * ---------------------------------------------------------------------------
 * P5 — view, roster, invite, remove-me.
 * ---------------------------------------------------------------------------
 */

export interface ViewPatch {
  readonly alias?: string | null
  readonly icon?: string
  readonly color?: string
  readonly sortOrder?: number
}

export async function patchView(
  request: ApiRequestFn,
  gateId: string,
  body: ViewPatch,
): Promise<ApiResult<unknown>> {
  const response = await request({
    method: ROUTES.gateView.method,
    path: buildPath(ROUTES.gateView, { gateId }),
    body,
  })
  return isSuccess(response.status) ? ok(response.status, response.body) : fail(response)
}

export interface RosterRow {
  readonly kind: 'member' | 'invite'
  readonly userId?: string
  readonly phone?: string
  readonly role: 'manager' | 'resident'
  readonly status: 'pending' | 'active' | 'expired'
  readonly displayName: string
  readonly installer: boolean
  readonly createdAt: string
  /**
   * §5.2's grant transport restriction (D269). **Absent is the third state, not a missing value** —
   * a grant that restricts no transport carries no such attribute (D46), which is what the member
   * sheet's "any" chip means. The roster has returned this since P5 (`members/roster.ts`); until
   * D269 nothing here read it.
   */
  readonly transportRestriction?: TransportKind
}

export interface Roster {
  readonly gateId: string
  readonly members: readonly RosterRow[]
  readonly unseenExpired: number
}

function readRosterRow(raw: unknown): RosterRow | undefined {
  const kind = field(raw, 'kind')
  const role = field(raw, 'role')
  const status = field(raw, 'status')
  const displayName = stringField(raw, 'displayName')
  const createdAt = stringField(raw, 'createdAt')
  if (
    (kind !== 'member' && kind !== 'invite') ||
    (role !== 'manager' && role !== 'resident') ||
    (status !== 'pending' && status !== 'active' && status !== 'expired') ||
    displayName === undefined
  ) {
    return undefined
  }
  const userId = stringField(raw, 'userId')
  const phone = stringField(raw, 'phone')
  // Narrowed rather than cast: an unrecognized value reads as *no restriction*, which is the
  // permissive answer — and the honest one, since the authority is `evaluateAccess()` server-side
  // and this row only decides which chip lights up.
  const restriction = field(raw, 'transportRestriction')
  return {
    kind,
    role,
    status,
    displayName,
    installer: field(raw, 'installer') === true,
    createdAt: createdAt ?? '',
    ...(userId === undefined ? {} : { userId }),
    ...(phone === undefined ? {} : { phone }),
    ...(restriction === 'cloud' || restriction === 'ble' ? { transportRestriction: restriction } : {}),
  }
}

export async function getRoster(request: ApiRequestFn, gateId: string): Promise<ApiResult<Roster>> {
  const response = await request({
    method: ROUTES.memberList.method,
    path: buildPath(ROUTES.memberList, { gateId }),
  })
  if (!isSuccess(response.status)) return fail(response)
  const members = field(response.body, 'members')
  const rows = Array.isArray(members)
    ? members.map(readRosterRow).filter((row): row is RosterRow => row !== undefined)
    : []
  return ok(response.status, {
    gateId,
    members: rows,
    unseenExpired: numberField(response.body, 'unseenExpired') ?? 0,
  })
}

export async function markRosterSeen(request: ApiRequestFn, gateId: string): Promise<void> {
  await request({
    method: ROUTES.memberSeen.method,
    path: buildPath(ROUTES.memberSeen, { gateId }),
  })
}

/** What `PATCH /gates/{id}/members/{memberId}` echoes back (`packages/functions/members/routes.ts`). */
export interface PatchMemberResult {
  readonly gateId: string
  readonly userId?: string
  readonly phone?: string
  readonly role: 'manager' | 'resident'
  readonly status: 'pending' | 'active' | 'expired'
}

/**
 * A grant edit. **Every field is optional and `undefined` means *leave it alone*** — the server's
 * `toGrantPatch` builds its update expression from exactly the keys present, so a body carrying
 * one field touches one field. `transportRestriction: null` is the one value that is not a value:
 * it is D46's explicit *clear this*, and it is how the member sheet's "any" chip removes a
 * restriction (D269). At least one field must be present, or the schema refuses the body.
 */
export interface GrantPatchBody {
  readonly role?: 'manager' | 'resident'
  readonly transportRestriction?: TransportKind | null
}

/**
 * `memberId` is a `UserId` and nothing else. **It used to address a pending invitation too**, as
 * a `+`-prefixed E.164 that `members/routes.ts` discriminated on — which put that number into an
 * API Gateway access log, and D236 split the two. Use {@link patchInvitation} for an invitation.
 */
export async function patchMember(
  request: ApiRequestFn,
  gateId: string,
  memberId: string,
  body: GrantPatchBody,
): Promise<ApiResult<PatchMemberResult>> {
  const response = await request({
    method: ROUTES.memberUpdate.method,
    path: buildPath(ROUTES.memberUpdate, { gateId, memberId }),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)
  const role = field(response.body, 'role')
  const status = field(response.body, 'status')
  if (role !== 'manager' && role !== 'resident') return fail(response)
  if (status !== 'pending' && status !== 'active' && status !== 'expired') return fail(response)
  const userId = stringField(response.body, 'userId')
  const phone = stringField(response.body, 'phone')
  return ok(response.status, {
    gateId,
    role,
    status,
    ...(userId === undefined ? {} : { userId }),
    ...(phone === undefined ? {} : { phone }),
  })
}

/** `DELETE /gates/{id}/members/{memberId}` — a manager's revoke of a **member** (§3.2). An
 * invitation is {@link revokeInvitation}. */
export async function revokeMember(
  request: ApiRequestFn,
  gateId: string,
  memberId: string,
): Promise<ApiResult<void>> {
  const response = await request({
    method: ROUTES.memberRevoke.method,
    path: buildPath(ROUTES.memberRevoke, { gateId, memberId }),
  })
  return isSuccess(response.status) ? ok(response.status, undefined) : fail(response)
}

/**
 * **D236 — the pending invitation's own pair, with the number in the body.**
 *
 * A `409 grant-not-editable` (an expired pending grant — §7.7's re-send is out of scope here)
 * comes back through `fail()`'s ordinary `code` field like any other failure; callers distinguish
 * it by reading `result.code` rather than this function inventing a second error shape for it.
 */
export async function patchInvitation(
  request: ApiRequestFn,
  gateId: string,
  phone: string,
  body: GrantPatchBody,
): Promise<ApiResult<PatchMemberResult>> {
  const response = await request({
    method: ROUTES.invitationUpdate.method,
    path: buildPath(ROUTES.invitationUpdate, { gateId }),
    body: { phone, ...body },
  })
  if (!isSuccess(response.status)) return fail(response)
  const role = field(response.body, 'role')
  const status = field(response.body, 'status')
  if (role !== 'manager' && role !== 'resident') return fail(response)
  if (status !== 'pending' && status !== 'active' && status !== 'expired') return fail(response)
  const echoed = stringField(response.body, 'phone')
  return ok(response.status, { gateId, role, status, ...(echoed === undefined ? {} : { phone: echoed }) })
}

/** `POST /gates/{id}/invitations/revoke` — D21's revoke-before-accept (D236). */
export async function revokeInvitation(
  request: ApiRequestFn,
  gateId: string,
  phone: string,
): Promise<ApiResult<void>> {
  const response = await request({
    method: ROUTES.invitationRevoke.method,
    path: buildPath(ROUTES.invitationRevoke, { gateId }),
    body: { phone },
  })
  return isSuccess(response.status) ? ok(response.status, undefined) : fail(response)
}

export interface InviteResult {
  readonly outcome: 'added' | 'pending' | 'skipped-duplicate'
  readonly share: { readonly text: string; readonly url: string }
}

/**
 * **`role` is required and has no default**, deliberately. P5 has accepted `manager` since it
 * shipped (`MEMBER_ROLES`, and the transactional `managerCount` write behind it), but this
 * function used to default to `resident` — so `InviteScreen` omitting the argument silently
 * produced a resident-only invite flow that looked like a server limitation. A caller must now
 * say which role it is granting, and forgetting is a compile error rather than a quiet downgrade.
 *
 * **`source` is optional and fleet-wide (D79, P14 §6)** — which control produced this invite,
 * `'picker'` or `'manual'`. Omitted, the server reports `unreported` rather than guessing; this
 * extends P5's existing field for `InviteScreen`'s single-invite flow rather than forking a
 * second one, the same instrument the bulk pipeline (`screens/invite/bulkApi.ts`) reports through.
 */
export async function inviteMember(
  request: ApiRequestFn,
  gateId: string,
  phone: string,
  locale: Locale,
  role: MemberRole,
  source?: 'picker' | 'manual',
): Promise<ApiResult<InviteResult>> {
  const response = await request({
    method: ROUTES.memberInvite.method,
    path: buildPath(ROUTES.memberInvite, { gateId }),
    body: { phone, role, locale, ...(source === undefined ? {} : { source }) },
  })
  if (!isSuccess(response.status)) return fail(response)
  const outcome = field(response.body, 'outcome')
  const share = field(response.body, 'share')
  const text = stringField(share, 'text')
  const url = stringField(share, 'url')
  if (
    (outcome !== 'added' && outcome !== 'pending' && outcome !== 'skipped-duplicate') ||
    text === undefined ||
    url === undefined
  ) {
    return fail(response)
  }
  return ok(response.status, { outcome, share: { text, url } })
}

export async function removeMyMembership(request: ApiRequestFn, gateId: string): Promise<ApiResult<void>> {
  const response = await request({
    method: ROUTES.memberRemoveSelf.method,
    path: buildPath(ROUTES.memberRemoveSelf, { gateId }),
  })
  return isSuccess(response.status) ? ok(response.status, undefined) : fail(response)
}

/**
 * `DELETE /gates/{gateId}` (D124-D129) — **manager-only, and asynchronous by design.** The
 * `202 {gateId, status:"deleting"}` means the root has been flipped and a stream worker will
 * finish the cleanup, removing the gate root and the BrokerCode claim *last*. A caller must not
 * read the absence of a `204` as a failure, and must not expect the gate to be gone from a read
 * issued immediately afterwards.
 *
 * `status` is read defensively like every other field here rather than narrowed to the literal:
 * the screen renders nothing from it, and a server that grows a third state must not break a
 * delete that the server has already accepted.
 */
export interface GateDeletion {
  readonly gateId: string
  readonly status?: string
}

export async function deleteGate(request: ApiRequestFn, gateId: string): Promise<ApiResult<GateDeletion>> {
  const response = await request({
    method: ROUTES.gateDelete.method,
    path: buildPath(ROUTES.gateDelete, { gateId }),
  })
  if (!isSuccess(response.status)) return fail(response)
  const status = stringField(response.body, 'status')
  return ok(response.status, { gateId, ...(status === undefined ? {} : { status }) })
}

/*
 * ---------------------------------------------------------------------------
 * P2 — the signed-in user's own profile.
 * ---------------------------------------------------------------------------
 */

/** What `PATCH /users/me` echoes back (`packages/functions/auth/routes.ts`). */
export interface ProfileView {
  readonly userId: string
  readonly name?: string
  readonly locale: Locale
}

/**
 * D42's self-name and locale. **There is no `GET /users/me`** — this response and D139's
 * `POST /auth/refresh` are the only two ways to read the profile outside a fresh sign-in, and
 * this is the only one a caller can ask for. Every caller must therefore hand what it returns
 * to `SessionManager.rememberProfile()` rather than trusting its own input: the refresh will
 * eventually correct a wrong local guess, but not before the user has seen it.
 *
 * A `422` means the server's `normalizeDisplayText` refused the value (contracts §11); callers
 * apply the same function before sending, so it should be unreachable rather than routine.
 */
export async function patchProfile(
  request: ApiRequestFn,
  body: { readonly name?: string; readonly locale?: Locale },
): Promise<ApiResult<ProfileView>> {
  const response = await request({
    method: ROUTES.profileUpdate.method,
    path: buildPath(ROUTES.profileUpdate),
    body,
  })
  if (!isSuccess(response.status)) return fail(response)
  const userId = stringField(response.body, 'userId')
  if (userId === undefined) return fail(response)
  const name = stringField(response.body, 'name')
  const rawLocale = stringField(response.body, 'locale')
  return ok(response.status, {
    userId,
    ...(name === undefined ? {} : { name }),
    // `isLocale` rather than an inline membership test: this is the fourth boundary that narrows
    // an unvalidated locale string, and the first three were spelled out by hand. Adding a locale
    // and missing one of them typechecks and then silently renders the account in Hebrew — which
    // is exactly what this line did to `ru` until a test caught it (D200).
    locale: isLocale(rawLocale) ? rawLocale : 'he',
  })
}

/*
 * ---------------------------------------------------------------------------
 * P9 — Activity (D29, manager-only) and D104's non-manager last-used stamp.
 * ---------------------------------------------------------------------------
 */

/**
 * One row of `GET /gates/{id}/activity` — every field an id, plus the one string the server
 * already resolved (`actorDisplayName`, §6). This screen renders `via`/`action`/`result`
 * through its own i18n catalog rather than trusting server prose, so an unrecognized `action`
 * degrades to a generic label instead of a crash (§5).
 */
export interface ActivityRow {
  readonly eventId: string
  readonly ts: string
  readonly action: string
  readonly result: 'success' | 'failure' | 'refused'
  readonly gateId?: string
  readonly actorUserId?: string
  readonly actorDisplayName?: string
  readonly via?: string
  readonly deviceId?: string
  readonly transport?: string
  readonly latencyMs?: number
  readonly scheduleTimeZone?: string
  readonly resolvedInstant?: string
  readonly failureReason?: string
  /** D25/PEC-12 — present only on a reconciled offline-BLE row. No P16 producer ships yet. */
  readonly reconciliationId?: string
}

export interface ActivityPage {
  readonly rows: readonly ActivityRow[]
  readonly nextCursor?: string
}

export interface ActivityFilters {
  readonly transport?: 'cloud' | 'ble'
  readonly result?: 'success' | 'failure' | 'refused'
  readonly user?: string
  readonly from?: string
  readonly to?: string
  readonly limit?: number
  readonly cursor?: string
}

function readActivityRow(raw: unknown): ActivityRow | undefined {
  const eventId = stringField(raw, 'eventId')
  const ts = stringField(raw, 'ts')
  const action = stringField(raw, 'action')
  const result = field(raw, 'result')
  if (
    eventId === undefined ||
    ts === undefined ||
    action === undefined ||
    (result !== 'success' && result !== 'failure' && result !== 'refused')
  ) {
    return undefined
  }
  const gateId = stringField(raw, 'gateId')
  const actorUserId = stringField(raw, 'actorUserId')
  const actorDisplayName = stringField(raw, 'actorDisplayName')
  const via = stringField(raw, 'via')
  const deviceId = stringField(raw, 'deviceId')
  const transport = stringField(raw, 'transport')
  const latencyMs = numberField(raw, 'latencyMs')
  const scheduleTimeZone = stringField(raw, 'scheduleTimeZone')
  const resolvedInstant = stringField(raw, 'resolvedInstant')
  const failureReason = stringField(raw, 'failureReason')
  const reconciliationId = stringField(raw, 'reconciliationId')
  return {
    eventId,
    ts,
    action,
    result,
    ...(gateId === undefined ? {} : { gateId }),
    ...(actorUserId === undefined ? {} : { actorUserId }),
    ...(actorDisplayName === undefined ? {} : { actorDisplayName }),
    ...(via === undefined ? {} : { via }),
    ...(deviceId === undefined ? {} : { deviceId }),
    ...(transport === undefined ? {} : { transport }),
    ...(latencyMs === undefined ? {} : { latencyMs }),
    ...(scheduleTimeZone === undefined ? {} : { scheduleTimeZone }),
    ...(resolvedInstant === undefined ? {} : { resolvedInstant }),
    ...(failureReason === undefined ? {} : { failureReason }),
    ...(reconciliationId === undefined ? {} : { reconciliationId }),
  }
}

/** `ApiRequest.path` carries no separate query field (`auth/transport.ts`) — built here, once. */
function activityQueryString(filters: ActivityFilters): string {
  const query = new URLSearchParams()
  if (filters.transport !== undefined) query.set('transport', filters.transport)
  if (filters.result !== undefined) query.set('result', filters.result)
  if (filters.user !== undefined) query.set('user', filters.user)
  if (filters.from !== undefined) query.set('from', filters.from)
  if (filters.to !== undefined) query.set('to', filters.to)
  if (filters.limit !== undefined) query.set('limit', String(filters.limit))
  if (filters.cursor !== undefined) query.set('cursor', filters.cursor)
  const qs = query.toString()
  return qs.length === 0 ? '' : `?${qs}`
}

/**
 * §7.6 — the manager-only filtered, paged history for one gate. A `403` means the viewer is
 * not this gate's manager (D29) and is rendered as the screen's own `forbidden` phase, the
 * same shape `getRoster`'s callers already use — never retried, never shown as a connectivity
 * problem.
 */
export async function getGateActivity(
  request: ApiRequestFn,
  gateId: string,
  filters: ActivityFilters = {},
): Promise<ApiResult<ActivityPage>> {
  const response = await request({
    method: ROUTES.gateActivity.method,
    path: buildPath(ROUTES.gateActivity, { gateId }) + activityQueryString(filters),
  })
  if (!isSuccess(response.status)) return fail(response)
  const rowsRaw = field(response.body, 'rows')
  const rows = Array.isArray(rowsRaw)
    ? rowsRaw.map(readActivityRow).filter((row): row is ActivityRow => row !== undefined)
    : []
  const nextCursor = stringField(response.body, 'nextCursor')
  return ok(response.status, { rows, ...(nextCursor === undefined ? {} : { nextCursor }) })
}

/**
 * **D337 — every tile's last-used stamp in ONE request**: `GET /activity?last=true`, the per-gate
 * read below lifted to the cross-gate route as the same parameter (§2a rule 3's *a parameter of
 * the reader, never a second reader*). The server answers for each gate the caller holds actively,
 * each with the caller's own last open only (D163); a gate it leaves out renders no stamp.
 *
 * **`data: undefined` on a `2xx` means *this server predates D337*, and it is the only thing that
 * does.** An older stage answers the same URL with the manager's cross-gate history — `rows`, no
 * `gates` — because `?last=true` meant nothing to it; so the shape, not the status, is what tells a
 * caller to fall back to the per-gate read. A native binary can run ahead of the stage it talks to,
 * which is why the fallback exists at all.
 */
export async function getLastUsedAtAll(
  request: ApiRequestFn,
): Promise<ApiResult<ReadonlyMap<string, string | null> | undefined>> {
  const response = await request({
    method: ROUTES.activity.method,
    path: `${buildPath(ROUTES.activity)}?last=true`,
  })
  if (!isSuccess(response.status)) return fail(response)
  const raw = field(response.body, 'gates')
  if (!Array.isArray(raw)) return ok(response.status, undefined)
  const stamps = new Map<string, string | null>()
  for (const entry of raw) {
    const gateId = stringField(entry, 'gateId')
    if (gateId === undefined) continue
    const lastUsedAt = field(entry, 'lastUsedAt')
    stamps.set(gateId, typeof lastUsedAt === 'string' ? lastUsedAt : null)
  }
  return ok(response.status, stamps)
}

/**
 * §2a/D104/D163 — the Home tile's bare last-used timestamp. **Any member, not just a manager**
 * (§2a rule 3): the same route as {@link getGateActivity}, parameterized by `?last=true`
 * rather than a second reader. Carries no actor — the response has no field to read one from —
 * and the server scopes the answer to the caller's own opens (D163), so a manager and a
 * resident calling this on the same gate get different timestamps.
 */
export async function getLastUsedAt(
  request: ApiRequestFn,
  gateId: string,
): Promise<ApiResult<string | null>> {
  const response = await request({
    method: ROUTES.gateActivity.method,
    path: `${buildPath(ROUTES.gateActivity, { gateId })}?last=true`,
  })
  if (!isSuccess(response.status)) return fail(response)
  const raw = field(response.body, 'lastUsedAt')
  return ok(response.status, typeof raw === 'string' ? raw : null)
}
